/**
 * Shared callback registry for blockchain events
 *
 * This file acts as a bridge between EvmPredictionMarketProvider and useBlockchainEvents
 * to avoid circular dependencies while allowing the provider to trigger user-specific callbacks.
 *
 * Architecture:
 * - useBlockchainEvents registers callbacks here on mount
 * - EvmPredictionMarketProvider triggers callbacks when events match current user
 * - Callbacks are cleaned up automatically when composables unmount
 *
 * This pattern allows real-time UI updates (success notifications, balance refresh)
 * without creating circular imports between provider and composable layers.
 */

/**
 * Callback function type for transaction success events
 * Invoked when user's transaction is confirmed on-chain (prediction placed, market created, etc.)
 */
type TransactionEventCallback = () => void;

/**
 * Callback registries - shared state between provider and composable
 * Each Set holds multiple callbacks to support multiple composable instances
 */
const predictionPlacedCallbacks = new Set<TransactionEventCallback>();
const marketCreatedCallbacks = new Set<TransactionEventCallback>();
const marketSettledCallbacks = new Set<TransactionEventCallback>();
const marketCancelledCallbacks = new Set<TransactionEventCallback>();
const creatorRegisteredCallbacks = new Set<TransactionEventCallback>();
const creatorUpdatedCallbacks = new Set<TransactionEventCallback>();
const tokenApprovedCallbacks = new Set<TransactionEventCallback>();
const tokenTransferredCallbacks = new Set<TransactionEventCallback>();
// Note: Native transfers (ETH/MATIC/etc) cannot be monitored via WebSocket - they don't emit events

/**
 * User address getter function - injected by useBlockchainEvents
 * Used by provider to check if events are from current user
 */
let getCurrentUserAddressFn: (() => string | null) | null = null;

/**
 * Balance refresh trigger - injected by useBlockchainEvents
 * Called after transactions to update user's token balance
 */
let triggerBalanceRefreshFn: (() => void) | null = null;

// ============================================================================
// Callback Registration (called by useBlockchainEvents composable)
// ============================================================================

/**
 * Register a callback to be invoked when user places a prediction
 * @param callback Function to call when PlacedPrediction event is from current user
 * @returns Cleanup function to unregister the callback
 */
export function registerPredictionPlacedCallback(callback: TransactionEventCallback): () => void {
  predictionPlacedCallbacks.add(callback);
  return () => predictionPlacedCallbacks.delete(callback);
}

/**
 * Register a callback to be invoked when user creates a market
 * @param callback Function to call when CreatedMarket event is from current user
 * @returns Cleanup function to unregister the callback
 */
export function registerMarketCreatedCallback(callback: TransactionEventCallback): () => void {
  marketCreatedCallbacks.add(callback);
  return () => marketCreatedCallbacks.delete(callback);
}

/**
 * Register a callback to be invoked when user settles a market
 * @param callback Function to call when SettledMarket event is from current user
 * @returns Cleanup function to unregister the callback
 */
export function registerMarketSettledCallback(callback: TransactionEventCallback): () => void {
  marketSettledCallbacks.add(callback);
  return () => marketSettledCallbacks.delete(callback);
}

/**
 * Register a callback to be invoked when user cancels a market
 * @param callback Function to call when CancelledMarket event is from current user
 * @returns Cleanup function to unregister the callback
 */
export function registerMarketCancelledCallback(callback: TransactionEventCallback): () => void {
  marketCancelledCallbacks.add(callback);
  return () => marketCancelledCallbacks.delete(callback);
}

/**
 * Register a callback to be invoked when user registers as a creator
 * @param callback Function to call when CreatedCreator event is from current user
 * @returns Cleanup function to unregister the callback
 */
export function registerCreatorRegisteredCallback(callback: TransactionEventCallback): () => void {
  creatorRegisteredCallbacks.add(callback);
  return () => creatorRegisteredCallbacks.delete(callback);
}

/**
 * Register a callback to be invoked when user updates their creator profile
 * @param callback Function to call when UpdatedCreator event is from current user
 * @returns Cleanup function to unregister the callback
 */
export function registerCreatorUpdatedCallback(callback: TransactionEventCallback): () => void {
  creatorUpdatedCallbacks.add(callback);
  return () => creatorUpdatedCallbacks.delete(callback);
}

// ============================================================================
// Helper Function Injection (called by useBlockchainEvents composable)
// ============================================================================

/**
 * Inject the function to get current user's wallet address
 * Called by useBlockchainEvents on initialization
 * @param fn Function that returns current user's address (or null if not connected)
 */
export function setGetCurrentUserAddressFn(fn: () => string | null): void {
  getCurrentUserAddressFn = fn;
}

/**
 * Inject the function to trigger balance refresh
 * Called by useBlockchainEvents on initialization
 * @param fn Function to refresh user's token balance after transactions
 */
export function setTriggerBalanceRefreshFn(fn: () => void): void {
  triggerBalanceRefreshFn = fn;
}

// ============================================================================
// Callback Triggering (called by EvmPredictionMarketProvider on events)
// ============================================================================

/**
 * Trigger all registered prediction placed callbacks
 * Called by EvmPredictionMarketProvider when PlacedPrediction event is from current user
 * Results in success notifications and UI updates
 */
export function triggerPredictionPlacedCallbacks(): void {
  predictionPlacedCallbacks.forEach((callback) => callback());
}

/**
 * Trigger all registered market created callbacks
 * Called by EvmPredictionMarketProvider when CreatedMarket event is from current user
 * Results in success notifications and navigation to market page
 */
export function triggerMarketCreatedCallbacks(): void {
  marketCreatedCallbacks.forEach((callback) => callback());
}

/**
 * Trigger all registered market settled callbacks
 * Called by EvmPredictionMarketProvider when SettledMarket event is from current user
 * Results in success notifications showing settlement results
 */
export function triggerMarketSettledCallbacks(): void {
  marketSettledCallbacks.forEach((callback) => callback());
}

/**
 * Trigger all registered market cancelled callbacks
 * Called by EvmPredictionMarketProvider when CancelledMarket event is from current user
 * Results in success notifications confirming cancellation
 */
export function triggerMarketCancelledCallbacks(): void {
  marketCancelledCallbacks.forEach((callback) => callback());
}

/**
 * Trigger all registered creator registered callbacks
 * Called by EvmPredictionMarketProvider when CreatedCreator event is from current user
 * Results in success notifications and navigation to creator profile
 */
export function triggerCreatorRegisteredCallbacks(): void {
  creatorRegisteredCallbacks.forEach((callback) => callback());
}

/**
 * Trigger all registered creator updated callbacks
 * Called by EvmPredictionMarketProvider when UpdatedCreator event is from current user
 * Results in success notifications confirming profile update
 */
export function triggerCreatorUpdatedCallbacks(): void {
  creatorUpdatedCallbacks.forEach((callback) => callback());
}

// ============================================================================
// Helper Functions (called by EvmPredictionMarketProvider)
// ============================================================================

/**
 * Get current user's wallet address
 * Used by EvmPredictionMarketProvider to check if events are from current user
 * @returns User's wallet address (lowercase) or null if not connected/not set
 */
export function getCurrentUserAddress(): string | null {
  return getCurrentUserAddressFn ? getCurrentUserAddressFn() : null;
}

/**
 * Trigger balance refresh after a delay
 * Called by EvmPredictionMarketProvider after user's transactions
 * Ensures UI shows updated token balance after predictions/approvals
 */
export function triggerBalanceRefreshWithDelay(): void {
  if (triggerBalanceRefreshFn) {
    triggerBalanceRefreshFn();
  }
}

// ============================================================================
// Token Event Callbacks (Approval, Transfer)
// ============================================================================

/**
 * Register a callback to be invoked when token approval is detected
 * @param callback - Function to call when approval event occurs
 * @returns Cleanup function to unregister the callback
 */
export function registerTokenApprovedCallback(callback: TransactionEventCallback): () => void {
  tokenApprovedCallbacks.add(callback);
  return () => {
    tokenApprovedCallbacks.delete(callback);
  };
}

/**
 * Register a callback to be invoked when token transfer is detected
 * @param callback - Function to call when transfer event occurs
 * @returns Cleanup function to unregister the callback
 */
export function registerTokenTransferredCallback(callback: TransactionEventCallback): () => void {
  tokenTransferredCallbacks.add(callback);
  return () => {
    tokenTransferredCallbacks.delete(callback);
  };
}

/**
 * Trigger all registered token approved callbacks
 * Called by EvmPredictionMarketProvider when Approval event is from current user
 */
export function triggerTokenApprovedCallbacks(): void {
  tokenApprovedCallbacks.forEach((callback) => callback());
}

/**
 * Trigger all registered token transferred callbacks
 * Called by EvmPredictionMarketProvider when Transfer event is from/to current user
 */
export function triggerTokenTransferredCallbacks(): void {
  tokenTransferredCallbacks.forEach((callback) => callback());
}
